/**
 * Générateur — Automatisme : placer sur une demi-droite graduée un point dont
 * l'abscisse est un nombre décimal.
 * Attendu ID : 1064 | Niveau : 5ème | Domaine : Repérage
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Place le point            (interactif placer_points : dixièmes, centièmes, unité partagée en 2 / 4 / 5)
 *   - Lire la graduation        (QCM « où se place… » ; saisie ; V/F sur une demi-droite avec points)
 *
 * ── Plan de cartes validé (2026-09-05) ─────────────────────────────────────
 * Source : Automatisme n°26 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * P1 : INTRODUIT au CM1 (fractions sur la demi-droite), CONSOLIDÉ au CM2
 * (décimal à virgule), ÉTENDU en 6e (encadrer, intercaler, graduer), automatisme
 * en 5e ; droite graduée complète et repère du plan FORMALISÉS en 5e (hors
 * gen). P2 : sens installés — la graduation comme partage de l'unité, le
 * décimal comme position ; 6e — intercaler, encadrer ; non installés —
 * abscisses négatives, coordonnées. Distracteurs : 2,7 pris pour 2,07 ou pour
 * la 27e graduation, 0,25 cru sur une graduation en dixièmes, abscisse
 * confondue avec une distance en cm. P3 : décimaux à une ou deux décimales
 * entre 0 et 10 ; pas 0,1 / 0,01 / 0,5 / 0,25 / 0,2. P5 : la demi-droite
 * graduée seule ; pas de repère, pas de négatifs. P6 : aucun instrument.
 * P7 : pas de droite graduée complète, pas de coordonnées, pas de fractions
 * non décimales. P8 : abscisse, origine, unité, graduation, pas. P9 : comparer
 * et encadrer des décimaux restent implicites. P10 : constat (« place »,
 * « où », « à quelle graduation », « combien »). P11 : les dixièmes ne
 * suffisent pas pour 0,25 ; l'unité en 4 fait tomber 2,5 mais pas 2,3 ; une
 * abscisse n'est pas une distance ; on ne place pas 4,1 sans prolonger.
 * P12 : rémanence forte.
 * INVARIANTS :
 *   - placer_points : le composant élève CALE sur les multiples du pas depuis
 *     0 → start multiple du pas, points sur la grille, jamais aux bornes ;
 *     seuls start, end et `reperes` sont étiquetés → les entiers sont passés
 *     en repères ; deux points par exercice ;
 *   - les figures des cartes 2-3 sont CONSTRUITES depuis (start, end, div,
 *     points) avec data-droite / data-points pour les solveurs ;
 *   - toute valeur est calculée en ENTIERS de la plus petite graduation
 *     (jamais de flottant comparé) ; V/F : pas de crochets, mixité 1-3 faux.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie, basePayload,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1064;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PLACER = 'Place le point';
const L_LIRE = 'Lire la graduation';

const LETTRES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'K', 'L', 'M', 'N', 'P', 'R', 'S', 'T'];
const fr = (x) => String(Number(x.toFixed(3))).replace('.', ',');
const ORD = (n) => (n === 1 ? '1re' : `${n}e`);

// ─── Carte 1 : place le point (interactif) ─────────────────────────────────

const makePlacer = (start, end, step, points, labels, label, reperes, intro) => {
    const bonne = {}; labels.forEach((l, i) => { bonne[l] = fr(points[i]); });
    const liste = labels.map((l, i) => `${l}(${fr(points[i])})`).join(' et ');
    const numberline = { start, end, step }; if (reperes.length) numberline.reperes = reperes;
    const payload = basePayload('association', `<p>${intro}</p><p>Place les points ${liste} sur la demi-droite graduée.</p>`, {
        subType: 'placer_points', numberline, pointsToPlace: labels, bonne_reponse: JSON.stringify(bonne),
    }, opts(label));
    return payload;
};

let derniereSignaturePlacer1064 = null;
const antiRepeat = (construire, cle) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignaturePlacer1064; t++) payload = construire();
    derniereSignaturePlacer1064 = payload._meta.params.signature;
    void cle;
    return payload;
};

/** Dixièmes : unité partagée en 10, 2 ou 3 unités, deux points à une décimale (un entier une fois sur quatre). */
const construirePlacerDixiemes = () => {
    const start = randInt(0, 7); const span = randomChoice([2, 3]); const end = start + span;
    const candidats = []; for (let i = 1; i < 10 * span; i++) if (i % 10 !== 0) candidats.push(i);
    const choisis = shuffle(candidats).slice(0, 2).map((i) => Math.round((start * 10 + i)) / 10);
    if (Math.random() < 0.25) choisis[0] = start + randInt(1, span - 1);
    const points = [...new Set(choisis)].sort((a, b) => a - b);
    if (points.length < 2) return construirePlacerDixiemes();
    const labels = shuffle(LETTRES).slice(0, 2);
    const reperes = []; for (let k = start + 1; k < end; k++) reperes.push(k);
    const payload = makePlacer(start, end, 0.1, points, labels, L_PLACER, reperes, 'Sur cette demi-droite graduée, chaque unité est partagée en 10.');
    payload.contenu = { ...payload.contenu, sousType: 'dixiemes' };
    payload._meta.params = { sousType: 'dixiemes', start, end, points, signature: `d-${start}-${points.join('/')}` };
    return payload;
};

/** Centièmes : un intervalle de 0,1 partagé en 10, deux points à deux décimales. */
const construirePlacerCentiemes = () => {
    const start = randInt(0, 99) / 10;                                // tout dixième de 0 à 9,9 (revue 2026-09-05, A8)
    const end = Math.round((start + 0.1) * 100) / 100;
    const idx = shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9]).slice(0, 2).sort((a, b) => a - b);
    const points = idx.map((i) => Math.round(start * 100 + i) / 100);
    const labels = shuffle(LETTRES).slice(0, 2);
    const payload = makePlacer(start, end, 0.01, points, labels, L_PLACER, [], `Sur cette demi-droite graduée, l'intervalle de ${fr(start)} à ${fr(end)} est partagé en 10.`);
    payload.contenu = { ...payload.contenu, sousType: 'centiemes' };
    payload._meta.params = { sousType: 'centiemes', start, end, points, signature: `c-${start}-${points.join('/')}` };
    return payload;
};

/** Unité partagée en 2, 4 ou 5 : points sur les graduations, jamais entiers. */
const PAS = [{ div: 2, step: 0.5 }, { div: 4, step: 0.25 }, { div: 5, step: 0.2 }];
const construirePlacerPas = () => {
    const { div, step } = randomChoice(PAS);
    const start = randInt(0, 6); const span = randomChoice([2, 3]); const end = start + span;
    const candidats = []; for (let i = 1; i < div * span; i++) if (i % div !== 0) candidats.push(i);
    const points = shuffle(candidats).slice(0, 2).sort((a, b) => a - b).map((i) => Math.round((start + i * step) * 100) / 100);
    const labels = shuffle(LETTRES).slice(0, 2);
    const reperes = []; for (let k = start + 1; k < end; k++) reperes.push(k);
    const payload = makePlacer(start, end, step, points, labels, L_PLACER, reperes, `Sur cette demi-droite graduée, chaque unité est partagée en ${div}.`);
    payload.contenu = { ...payload.contenu, sousType: `pas${div}` };
    payload._meta.params = { sousType: `pas${div}`, div, start, end, points, signature: `p${div}-${start}-${points.join('/')}` };
    return payload;
};

const genPlacerDixiemes = () => antiRepeat(construirePlacerDixiemes);
const genPlacerCentiemes = () => antiRepeat(construirePlacerCentiemes);
const genPlacerPas = () => antiRepeat(construirePlacerPas);

// ─── Rendu : demi-droite graduée (cartes 2 et 3) ───────────────────────────

/**
 * cfg = { start, end, div (graduations par unité), points: [{x, nom}], etiquettes: valeurs à écrire }
 * L'origine du dessin est start ; U px par unité, choisi pour tenir en 320 px.
 */
const svgDemiDroite = (cfg) => {
    const { start, end, div } = cfg; const span = end - start;
    const U = Math.min(90, Math.floor(280 / span)); const x0 = 18; const y = 44;
    const X = (v) => x0 + (v - start) * U;
    let s = `<line x1="${x0 - 6}" y1="${y}" x2="${X(end) + 22}" y2="${y}" stroke="#1e293b" stroke-width="1.6"/>`
        + `<polygon points="${X(end) + 22},${y} ${X(end) + 14},${y - 4} ${X(end) + 14},${y + 4}" fill="#1e293b"/>`;
    const n = span * div;
    for (let i = 0; i <= n; i++) {
        const v = start + i / div; const gros = i % div === 0;
        s += `<line x1="${X(v).toFixed(1)}" y1="${y - (gros ? 9 : 5)}" x2="${X(v).toFixed(1)}" y2="${y + (gros ? 9 : 5)}" stroke="#1e293b" stroke-width="${gros ? 1.6 : 1}"/>`;
        if (gros) s += `<text x="${X(v).toFixed(1)}" y="${y + 24}" font-size="12" font-weight="700" fill="#1e293b" text-anchor="middle">${fr(v)}</text>`;
    }
    (cfg.points || []).forEach((p) => {
        s += `<circle cx="${X(p.x).toFixed(1)}" cy="${y}" r="3.6" fill="#B45309" data-point="${p.nom}"/>`
            + `<text x="${X(p.x).toFixed(1)}" y="${y - 15}" font-size="13" font-weight="700" fill="#B45309" text-anchor="middle">${p.nom}</text>`;
    });
    const w = X(end) + 30;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} 76" width="${w}" height="76" font-family="Arial" data-droite="${start};${end};${div}" data-points="${(cfg.points || []).map((p) => `${p.nom}:${p.x}`).join('|')}">${s}</svg>`;
};

// ─── Carte 2a : lire la graduation (QCM) ───────────────────────────────────

let derniereQuestionLire1064 = null;

// Longueurs des options homogénéisées (revue 2026-09-05, A2 : la bonne était la plus longue à 50 %).
const genLireQcm = () => {
    let cas = randomChoice(['dixieme', 'centieme', 'quarts_hors', 'quarts_sur']);
    if (cas === derniereQuestionLire1064) cas = randomChoice(['dixieme', 'centieme', 'quarts_hors', 'quarts_sur'].filter((c) => c !== cas));
    derniereQuestionLire1064 = cas;
    let texte; let bonne; let leurres; let svg; let x;
    if (cas === 'dixieme') {
        const k = randInt(0, 3); const d = randomChoice([2, 3, 4, 6, 7, 8]); x = k + d / 10;
        svg = svgDemiDroite({ start: 0, end: 4, div: 10 });
        texte = `<p>Sur cette demi-droite graduée, chaque unité est partagée en 10.</p><p>Où se place le point d'abscisse ${fr(x)} ?</p>`;
        bonne = `Entre ${k} et ${k + 1}, à la ${ORD(d)} petite graduation après ${k}.`;
        leurres = [`Entre ${k} et ${k + 1}, à la ${ORD(10 - d)} petite graduation après ${k}.`, `Entre ${k} et ${k + 1}, exactement au milieu.`, `Juste après ${k}, à la toute première petite graduation.`];
    } else if (cas === 'centieme') {
        const k = randInt(0, 3); const d = randInt(0, 9); x = k + d / 10 + 0.05;
        const bas = fr(k + d / 10); const haut = fr(k + (d + 1) / 10);
        svg = svgDemiDroite({ start: 0, end: 4, div: 10 });
        texte = `<p>Sur cette demi-droite graduée, chaque unité est partagée en 10.</p><p>Où se place le point d'abscisse ${fr(x)} ?</p>`;
        bonne = `Entre ${bas} et ${haut}, exactement au milieu, sur aucune graduation.`;
        leurres = [`Exactement sur la graduation ${bas}, la ${ORD(d === 0 ? 10 : d)} petite graduation après ${d === 0 ? k - 1 : k}.`, `Entre ${k} et ${k + 1}, à la ${ORD(d + 1)} petite graduation après ${k}.`, `Sur la ${d * 10 + 5}e petite graduation après ${k}, il faut prolonger.`];
        if (d === 0 && k === 0) leurres[0] = 'Exactement sur l\'origine, en 0.';
    } else if (cas === 'quarts_hors') {
        const k = randInt(0, 3); const d = randomChoice([1, 2, 3]); const a = k + d / 10;   // 2,3 : entre 2,25 et 2,5
        x = a; const bas = fr(k + Math.floor(a * 4 - k * 4) / 4); const haut = fr(k + (Math.floor(a * 4 - k * 4) + 1) / 4);
        svg = svgDemiDroite({ start: 0, end: 4, div: 4 });
        texte = `<p>Sur cette demi-droite graduée, chaque unité est partagée en 4.</p><p>Où se place le point d'abscisse ${fr(x)} ?</p>`;
        bonne = `Entre ${bas} et ${haut}, sur aucune graduation.`;
        leurres = [`Sur la ${ORD(d)} graduation après ${k}, exactement.`, `Exactement sur la graduation ${bas}.`, `Entre ${haut} et ${fr(Number(haut.replace(',', '.')) + 0.25)}, tout près de la graduation ${haut}.`];
    } else {
        const k = randInt(0, 3); const q = randomChoice([1, 2, 3]); x = k + q / 4;   // 2,5 : 2e graduation après 2
        svg = svgDemiDroite({ start: 0, end: 4, div: 4 });
        texte = `<p>Sur cette demi-droite graduée, chaque unité est partagée en 4.</p><p>Où se place le point d'abscisse ${fr(x)} ?</p>`;
        bonne = `Exactement sur la ${ORD(q)} graduation après ${k}.`;
        leurres = [`Exactement sur la ${ORD(q === 2 ? 5 : q === 1 ? 25 : 75)} graduation après ${k}.`, `Entre la ${ORD(q)} et la ${ORD(q + 1)} graduation après ${k}, sur aucune graduation.`, `Entre ${k + 1} et ${k + 2}, à la ${ORD(q)} graduation après ${k + 1}.`];
    }
    const options = shuffle([bonne, ...leurres]);
    const payload = makeQcm(options, bonne, texte, { ...opts(L_LIRE), elements_additionnels: imgElement(svg, 'large') });
    payload.contenu = { ...payload.contenu, sousType: cas };
    payload._meta.params = { cas, x, signature: `${cas}-${x}` };
    return payload;
};

// ─── Carte 2b : lire la graduation (saisie) ────────────────────────────────

let derniereSignatureSaisie1064 = null;

// Revue 2026-09-06 : les questions « écart entre deux graduations », « abscisse de la Ne graduation »
// et « abscisse du milieu de deux graduations » étaient de la LECTURE (elles donnent un nombre à partir
// d'une position : gen 1065, ou QCM centième de ce gen dans le bon sens). Reste la seule question qui
// prépare le geste de PLACER (fiche 26, « à quelle graduation exactement ? ») : compter les graduations.
const genLireSaisie = () => {
    let k; let d; let signature;
    for (let t = 0; t < 8; t++) {
        k = randInt(0, 3); d = randInt(1, 9); signature = `combien-${k}x${d}`;
        if (signature !== derniereSignatureSaisie1064) break;
    }
    derniereSignatureSaisie1064 = signature;
    const svg = svgDemiDroite({ start: 0, end: 4, div: 10 });
    const texte = `<p>Sur cette demi-droite graduée, chaque unité est partagée en 10.</p><p>Pour placer le point d'abscisse ${fr(k + d / 10)}, combien de petites graduations compte-t-on après ${k} ?</p>`;
    const payload = makeSaisie(String(d), texte, { ...opts(L_LIRE), suffixe: 'graduations', elements_additionnels: imgElement(svg, 'large') });
    payload.contenu = { ...payload.contenu, sousType: 'combien' };
    payload._meta.params = { cas: 'combien', k, d, reponse: String(d), signature };
    return payload;
};

// ─── Carte 2c : V/F sur une demi-droite avec des points ────────────────────

let dernieresLignesVfDroite1064 = [];

const genLireVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        // trois points A, B, C sur des dixièmes (en entiers de dixièmes, 1 à 39), non entiers, distincts
        const dix = shuffle(Array.from({ length: 39 }, (_, i) => i + 1).filter((i) => i % 10 !== 0)).slice(0, 3).sort((a, b) => a - b);
        if (dix[1] - dix[0] < 4 || dix[2] - dix[1] < 4) continue;   // étiquettes A, B, C jamais superposées
        const noms = ['A', 'B', 'C'];
        const pts = dix.map((v, i) => ({ x: v / 10, nom: noms[i], dix: v }));
        const P = randomChoice(pts); const Q = randomChoice(pts.filter((p) => p !== P));
        // nombre à deux décimales voisin d'un point : avant ou après
        const voisin = Math.round(P.dix * 10 + randomChoice([-4, -3, -2, 2, 3, 4])) / 100;
        const kEnt = Math.floor(P.x);
        const candidates = [
            // Revue 2026-09-06 : « L'abscisse de P est … » était de la lecture (gen 1065). Le piège 2,7 / 2,07
            // de la fiche 26 est gardé sous forme de GESTE : combien de petites graduations on compte.
            { l: `Pour placer le point d'abscisse ${fr(P.x)}, on compte ${P.dix % 10} petites graduations après ${kEnt}.`, v: true },
            { l: `Pour placer le point d'abscisse ${fr(kEnt + (P.dix % 10) / 100)}, on compte ${P.dix % 10} petites graduations après ${kEnt}.`, v: false },   // 2,07 est entre 2 et 2,1
            { l: `Le point d'abscisse ${fr(voisin)} se place avant ${P.nom}.`, v: voisin < P.x },
            { l: `Le point d'abscisse ${fr(voisin)} se place après ${P.nom}.`, v: voisin > P.x },
            { l: `${P.nom} est plus loin de l'origine que ${Q.nom}.`, v: P.x > Q.x },
            { l: `Le point d'abscisse ${fr(Math.round(randInt(1, 39)) / 10)} tombe exactement sur une graduation.`, v: true },
            { l: `Le point d'abscisse ${fr(randInt(0, 3) + randInt(0, 9) / 10 + 0.05)} tombe exactement sur une graduation.`, v: false },
            { l: `On peut placer le point d'abscisse ${fr(4 + randInt(1, 9) / 10)} sans prolonger cette demi-droite.`, v: false },
            { l: `On peut placer le point d'abscisse ${fr(3 + randInt(1, 9) / 10)} sans prolonger cette demi-droite.`, v: true },
        ];
        const fausses = shuffle(candidates.filter((it) => !it.v)); const vraies = shuffle(candidates.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (lignes.length !== 4) continue;
        // familles : lecture de P (0, 1), avant / après (2, 3), graduation (5, 6), prolonger (7, 8)
        const familles = [[0, 1], [2, 3], [5, 6], [7, 8]];
        if (familles.some(([i, j]) => lignes.includes(candidates[i]) && lignes.includes(candidates[j]))) continue;
        if (essai > 120) dernieresLignesVfDroite1064 = [];
        if (lignes.some((it) => dernieresLignesVfDroite1064.includes(it.l))) continue;
        dernieresLignesVfDroite1064 = lignes.map((it) => it.l);
        const svg = svgDemiDroite({ start: 0, end: 4, div: 10, points: pts });
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Sur cette demi-droite graduée, chaque unité est partagée en 10. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            { ...opts(L_LIRE), elements_additionnels: imgElement(svg, 'large') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { points: pts.map((p) => p.x) };
        return payload;
    }
    throw new Error('gen_1064 : V/F droite sans tirage après 160 essais');
};

// (Le V/F de pur rappel « Abscisse : vrai ou faux ? » a été retiré à la demande du prof, 2026-09-05.)

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    placer_dixiemes_placer_points: genPlacerDixiemes,
    placer_centiemes_placer_points: genPlacerCentiemes,
    placer_pas_placer_points: genPlacerPas,
    lire_qcm_unique: genLireQcm,
    lire_saisie: genLireSaisie,
    lire_tableau_vf: genLireVf,
};

const FORMAT_LABELS = { dixiemes_placer_points: 'Dixièmes', centiemes_placer_points: 'Centièmes', pas_placer_points: 'Unité en 2, 4 ou 5', saisie: 'Saisie', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'placer', label: L_PLACER, formatsList: buildFormats('placer') },
    { id: 'lire', label: L_LIRE, formatsList: buildFormats('lire') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
